<template>
    <CompassPanel :modelValue="showPanel" :title="panelTitle" @closed="closePanel">
        <template #default>
            <div v-if="effectiveTemplate && currentStep" class="automation-wizard-panel__content">
                <AutomationWizardOverviewContainer
                    v-if="currentStep.type === 'overview'"
                    :template="effectiveTemplate"
                    :isEditMode="isEditMode"
                />
                <AutomationWizardForm
                    v-else
                    v-model="formData"
                    :currentStep="currentStep"
                    @field-change="clearFieldError"
                />
            </div>
        </template>
        <template #footer>
            <AutomationWizardPanelFooter
                :allSteps="allSteps"
                :currentStepIndex="currentStepIndex"
                :isLoading="isLoadingActivate"
                :isEditMode="isEditMode"
                :readOnly="readOnly"
                :template="effectiveTemplate"
                @activate-automation="handleActivateAutomation"
                @previous-step="goToPreviousStep"
                @next-step="handleNextStep"
            />
        </template>
    </CompassPanel>
</template>

<script>
import AutomationMixin from 'mixins/automation/AutomationMixin.js';
import AutomationWizardOverviewContainer from 'vcomponents/automation/templates/Wizard/Overview/AutomationWizardOverviewContainer.vue';
import AutomationWizardForm from 'vcomponents/automation/templates/Wizard/Form/AutomationWizardForm.vue';
import AutomationWizardPanelFooter from 'vcomponents/automation/templates/Wizard/AutomationWizardPanelFooter.vue';

import {
    useAutomationTemplate,
    enableAutomation,
    updateAutomation,
    getAutomationTemplates,
} from 'api/automation/automationService.js';

export default {
    components: {
        AutomationWizardOverviewContainer,
        AutomationWizardForm,
        AutomationWizardPanelFooter,
    },
    mixins: [AutomationMixin],
    props: {
        template: {
            type: Object,
            default: null,
        },
        automation: {
            type: Object,
            default: null,
        },
        isEditMode: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            translations: {
                title: this.$t.__('automation.AutomationWizardPanel.title'),
                titleEdit: this.$t.__('automation.AutomationWizardPanel.title_edit'),
            },
            isLoadingActivate: false,
            formData: {},
            currentStepIndex: 0,
            showPanel: false,
            loadedTemplate: null,
        };
    },
    computed: {
        allSteps() {
            return this.effectiveTemplate?.metadata?.wizard_schema || [];
        },
        currentStep() {
            return this.allSteps[this.currentStepIndex] || null;
        },
        panelTitle() {
            return this.isEditMode ? this.translations.titleEdit : this.translations.title;
        },
        effectiveTemplate() {
            if (this.isEditMode && this.automation?.metadata) {
                return {
                    id: this.automation.id,
                    metadata: this.automation.metadata,
                    title:
                        this.automation.version?.displayName ||
                        this.automation.metadata?.template.title,
                    description: this.automation.metadata?.template?.description || '',
                    pieces: this.extractPiecesFromAutomation(),
                };
            }
            return this.loadedTemplate || this.template;
        },
        isOpenedFromExternalContext() {
            return !!this.template?.internal_id;
        },
    },
    watch: {
        $route() {
            this.closePanel();
        },
        template() {
            this.resetPanel();
        },
        automation() {
            this.resetPanel();
        },
        showPanel(newValue) {
            if (newValue) {
                this.resetPanel();
            }
        },
    },
    created() {
        if (this.isOpenedFromExternalContext) {
            this.loadTemplateByInternalId();
        } else {
            this.initializeFormData();
        }
    },
    mounted() {
        this.showPanel = true;
        document.addEventListener('keydown', this.handleEscapeKey);
    },
    unmounted() {
        document.removeEventListener('keydown', this.handleEscapeKey);
    },
    methods: {
        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.showPanel) {
                this.closePanel();
            }
        },
        resetPanel() {
            this.currentStepIndex = 0;
            this.clearAllErrors();
            this.initializeFormData();
        },
        closePanel() {
            this.clearAllErrors();
            this.showPanel = false;
            this.$nextTick(() => this.$emit('close'));
        },
        activateAutomation(id) {
            return enableAutomation(id)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('automation.common.success.start_automation'),
                    });
                })
                .catch((error) => {
                    let textToast = this.$t.__('automation.common.error.stop_automation');
                    if (error.response.status === 409) {
                        textToast = this.$t.__(
                            'automation.automationListingTableRow.toast.error.wait_minute',
                        );
                    }
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: textToast,
                    });
                    throw error;
                });
        },
        async handleActivateAutomation() {
            this.isLoadingActivate = true;
            try {
                if (this.isEditMode) {
                    await this.updateExistingAutomation();
                    this.navigateToListing();
                } else {
                    await this.createNewAutomation();
                    if (this.isOpenedFromExternalContext) {
                        this.closePanel();
                    } else {
                        this.navigateToListing();
                    }
                }
            } catch (error) {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: this.$t.__('common.labels.toast.error'),
                });
                throw error;
            } finally {
                this.isLoadingActivate = false;
            }
        },
        async createNewAutomation() {
            const preparedData = this.prepareFormDataForSubmission(
                this.effectiveTemplate,
                this.template,
                this.formData,
            );
            const result = await useAutomationTemplate(this.effectiveTemplate.id, preparedData);
            await this.activateAutomation(result.data.id);
        },
        async updateExistingAutomation() {
            const preparedData = this.prepareFormDataForSubmission(
                this.effectiveTemplate,
                this.template,
                this.formData,
            );
            await updateAutomation(this.automation.id, preparedData);
            this.$pushToast({
                type: 'success',
                close: true,
                text: this.$t.__('automation.common.success.automation_updated'),
            });
        },
        navigateToListing() {
            this.closePanel();
            if (this.isEditMode) {
                EventBus.emit('automation-updated');
            } else {
                this.$router.push({ name: 'automations.listing' });
            }
        },
        goToPreviousStep() {
            if (this.currentStepIndex > 0) {
                this.currentStepIndex--;
            }
        },
        goToNextStep() {
            if (this.currentStepIndex < this.allSteps.length - 1) {
                this.currentStepIndex++;
            }
        },
        handleNextStep() {
            this.isLoadingActivate = true;
            const isLastStep = this.currentStepIndex === this.allSteps.length - 1;
            if (isLastStep) {
                if (this.validateCurrentStep()) {
                    this.handleActivateAutomation();
                } else {
                    this.isLoadingActivate = false;
                }
            } else if (this.validateCurrentStep()) {
                this.isLoadingActivate = false;
                this.goToNextStep();
            } else {
                this.isLoadingActivate = false;
            }
        },
        validateCurrentStep() {
            if (!this.currentStep || !this.currentStep.fields) {
                return true;
            }
            for (const field of this.currentStep.fields) {
                delete field.currentError;
            }
            const requiredFields = this.currentStep.fields.filter(
                (field) => field.required && field.type !== 'hidden',
            );
            let hasErrors = false;
            for (const field of requiredFields) {
                const fieldValue = this.formData[field.id];
                const isEmpty = this.isFieldValueEmpty(fieldValue);

                if (isEmpty) {
                    field.currentError = field.props?.formItem?.error;
                    hasErrors = true;
                }
            }

            hasErrors |= !this.validateNumericFields();

            return !hasErrors;
        },
        validateNumericFields() {
            const numericFields = this.currentStep.fields.filter(
                (field) => field.type === 'percent' || field.type === 'amount',
            );

            for (const field of numericFields) {
                const fieldValue = this.formData[field.id];
                if (fieldValue !== '' && fieldValue !== null && fieldValue !== undefined) {
                    const numValue = Number(fieldValue);
                    // Min default=0
                    const minValue = field.props?.formItem?.min ?? 0;
                    if (!isNaN(numValue) && minValue > numValue) {
                        field.currentError = this.$t.__('common.errors.min_value', { min: minValue });
                        return false;
                    }
                    if (
                        field.props?.formItem?.max !== undefined &&
                        !isNaN(numValue) &&
                        numValue > field.props.formItem.max
                    ) {
                        field.currentError = this.$t.__('common.errors.max_value', { max: field.props.formItem.max });
                        return false;
                    }
                }
            }

            return true;
        },
        isFieldValueEmpty(value) {
            return !value || (Array.isArray(value) && value.length === 0);
        },
        clearFieldError(fieldName) {
            if (!this.currentStep || !this.currentStep.fields) {
                return;
            }
            const field = this.currentStep.fields.find((f) => f.id === fieldName);
            if (field && field.currentError) {
                delete field.currentError;
            }
        },
        clearAllErrors() {
            if (!this.effectiveTemplate?.metadata?.wizard_schema) {
                return;
            }
            for (const step of this.effectiveTemplate.metadata.wizard_schema) {
                if (step.fields) {
                    for (const field of step.fields) {
                        if (field.currentError) {
                            delete field.currentError;
                        }
                    }
                }
            }
        },
        extractPiecesFromAutomation() {
            if (this.automation?.pieces && Array.isArray(this.automation.pieces)) {
                return this.automation.pieces;
            }
            const pieces = new Set();
            if (this.automation?.version?.trigger?.settings?.pieceName) {
                pieces.add(this.automation.version.trigger.settings.pieceName);
            }
            if (this.automation?.metadata?.wizard_schema) {
                for (const step of this.automation.metadata.wizard_schema) {
                    if (step.stepName && step.stepName.includes('@activepieces/piece-')) {
                        const match = step.stepName.match(/@activepieces\/piece-[^/]+/);
                        if (match) {
                            pieces.add(match[0]);
                        }
                    }
                }
            }
            return Array.from(pieces);
        },
        async loadTemplateByInternalId() {
            const internalId = this.template?.internal_id;
            if (!internalId) {
                return;
            }
            try {
                const response = await getAutomationTemplates();
                const templates = response.data.data;
                const matchingTemplate = templates.find(
                    (template) => template.metadata?.template?.internal_id === internalId,
                );
                if (matchingTemplate) {
                    this.loadedTemplate = {
                        id: matchingTemplate.id,
                        title: matchingTemplate.name,
                        description: matchingTemplate.description,
                        version: matchingTemplate.template,
                        metadata: matchingTemplate.metadata,
                        tags: matchingTemplate.tags || [],
                    };
                    this.initializeFormData();
                }
            } catch (error) {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: this.$t.__('common.labels.toast.error'),
                });
                throw error;
            }
        },
        initializeFormData() {
            if (!this.effectiveTemplate?.metadata?.wizard_schema) {
                this.formData = {};
                return;
            }
            const newFormData = {};
            for (const step of this.effectiveTemplate.metadata.wizard_schema) {
                if (step.fields) {
                    for (const field of step.fields) {
                        let initialValue = '';
                        if (field.isMappedField && field.value !== undefined) {
                            initialValue = field.value;
                        } else if (field.props?.select?.value !== undefined) {
                            initialValue = field.props.select.value;
                        } else if (field.props?.iconButton?.value !== undefined) {
                            initialValue = field.props.iconButton.value;
                        } else if (field.props?.input?.value !== undefined) {
                            initialValue = field.props.input.value;
                        }
                        newFormData[field.id] = initialValue;
                    }
                }
            }

            this.formData = newFormData;
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-panel {
    &__content {
        display: flex;
        flex-direction: column;
    }
}
</style>
